import PrivacyPolicyPage from 'communitypage/privacypolicy/page/PrivacyPolicyPage';
import PrivacyPolicyWritePage from 'communitypage/privacypolicy/page/PrivacyPolicyWritePage';
import CraPage from 'communitypage/regulation/page/CraPage';
import PpwrPage from 'communitypage/regulation/page/PpwrPage';
import Footer from 'footer/Footer';
import { useScrollOver } from 'hooks/useScrollOver';
import { MainWrap } from 'mainpage/MainWrap';
import React from 'react';
import { Outlet, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { useSvrInfo } from 'store/reducers/reducerMain';
import { useDisplayBottomPopup, useDisplayPopupItem } from 'store/reducers/useDisplayPopup';
import styled from 'styled-components';
import { useZstdInfo } from 'zstd/useZstdInfo';
import { useWinResize } from 'zstd/useZstdWinResize';
import Header from '../header/Header';
import { SsqImages } from '../images/SsqImages';
import LoginCallbackPage from '../LoginCallbackPage';
import NotFoundPage from '../NotFoundPage';
import { UrlLinkPage } from '../pages/urllink/UrlLinkPage';
import { RootLayout } from '../RootLayout';
import { fontFamilyBold, fontFamilyMedium, fontFamilyRegular } from '../style/fonts';

const HeaderPopup = React.lazy(() => import('../header/popup/HeaderPopup').then((m) => ({ default: m.HeaderPopup })));
const MainHeader = React.lazy(() => import('../mainpage/header/MainHeader'));

// Asset External Redirect (기존 내부 Asset insight route는 삭제)
const AssetExternalRedirect = React.lazy(() => import('pages/asset/AssetExternalRedirect'));
const AssetInsight = React.lazy(() => import('servicePage/asset/AssetInsight'));

const CenterPopup = React.lazy(() => import('popup/comp/center/CenterPopup'));
const BottomPopup = React.lazy(() => import('../popup/comp/bottom/BottomPopup'));
const PartnerQnaPagePopup = React.lazy(() => import('communitypage/partner/qna/PartnerQnaPagePopup'));

const DispPreRegist = React.lazy(() => import('../pages/disp/DispPreRegist'));
const TestLayout = React.lazy(() => import('../tests/TestLayout').then((module) => ({ default: module.TestLayout })));
const UnsupportedBrowserPage = React.lazy(() => import('pages/unsupportbrowser/UnsupportedBrowserPage'));
const NaverLoginCallBackPage = React.lazy(() => import('../header/login/NaverLoginCallBackPage'));

const RequireMemberLoginPage = React.lazy(() => import('../login/RequireMemberLoginPage'));
const RequireEmployeeLoginPage = React.lazy(() => import('../login/RequireEmployeeLoginPage'));
const RequirePartnerLoginPage = React.lazy(() => import('../login/RequirePartnerLoginPage'));

const SocialSignupPage = React.lazy(() => import('header/popup/SocialSignupPage'));
const ResetPasswordPage = React.lazy(() => import('../header/popup/ResetPasswordPage'));

const AdminEventDocumentListPage = React.lazy(() => import('../borispage/event/AdminEventDocumentListPage'));
const AdminNewEventDocumentPage = React.lazy(() => import('../borispage/event/AdminNewEventDocumentPage'));
const AdminEventDocumentListReviewPage = React.lazy(
  () => import('../borispage/event/AdminEventDocumentListReviewPage')
);
const EventReviewPopupPage = React.lazy(() => import('../borispage/event/EventReviewPopupPage'));

const ProductPage = React.lazy(() => import('../productpage/ProductPage'));
const ManualPage = React.lazy(() => import('../qrmanual/ManualPage'));

const AllSearchPage2 = React.lazy(() => import('search2/AllSearchPage2'));
const AllSearchPage3 = React.lazy(() => import('search3/AllSearchPage3'));

const ProdDocDetailPage = React.lazy(() => import('../pages/product/detail/ProdDocDetailPage'));
const VerifyEmailPage = React.lazy(() => import('header/popup/VerifyEmailPage'));
const SolutionPage = React.lazy(() => import('../solutionpage/SolutionPage'));

const SolutionPagePopup = React.lazy(() => import('../solutionpage/SolutionPagePopup'));
const ProfilePage = React.lazy(() => import('../mypage/ProfilePage'));

const CommunityPage = React.lazy(() => import('../communitypage/CommunityPage'));
const CmmntyDocWritePage = React.lazy(() => import('docwrite/community/CmmntyDocWritePage'));

const QnaPagePopup = React.lazy(() => import('communitypage/qna/popup/QnaPagePopup'));
const BlogPagePopup = React.lazy(() => import('communitypage/blog/BlogPagePopup'));
const BoardPagePopup = React.lazy(() => import('../communitypage/board/BoardPagePopup'));

const AdminPage2 = React.lazy(() => import('borispage/AdminPage2'));

const ServicePage = React.lazy(() => import('../servicePage/ServicePage'));
const SamplePagePopup = React.lazy(() => import('servicePage/sample/SamplePagePopup'));
const SwServicePagePopup = React.lazy(() => import('servicePage/swService/SwServicePagePopup'));
const AdminSwRegisterPage = React.lazy(() => import('../borispage/admintab/swtab/AdminSwRegisterPage'));

const MainPage = React.lazy(() => import('../mainpage/MainPage'));

const ProductSelectorPubl = React.lazy(() => import('pages/prodselector/publ/ProductSelectorPubl'));
const ProductSelector = React.lazy(() => import('pages/prodselector/ProductSelector'));
const ProductSelectorV2 = React.lazy(() => import('pages/prodselector-v2/ProductSelectorV2'));
const DrivePanelSelector = React.lazy(() => import('pages/new-drive-selector/DriveSelector'));
const EnergySave = React.lazy(() => import('servicePage/energySave/EnergySave'));
const SwService = React.lazy(() => import('servicePage/swService/SwServicePage'));

const DlCenterPage = React.lazy(() => import('pages/dlcenter/DlCenterPage'));
const PtnrPageV2 = React.lazy(() => import('communitypage/partner-v2/PtnrPageV2'));
const TechSupportDetailPage = React.lazy(() => import('communitypage/partner-v2/tech-detail/TechSupportDetailPage'));
const SvcDocDetailPage = React.lazy(() => import('communitypage/partner-v2/svc-detail/SvcDocDetailPage'));

const PurchasePage = React.lazy(() => import('pages/servo/PurchasePage'));

// React Router v7 Layout Components using Outlet

export const WithHeaderLayout = () => {
  const { popupEnabled } = useDisplayPopupItem();
  const { displayBottomPopup } = useDisplayBottomPopup();

  return (
    <>
      <Header />
      <HeaderPopup />
      <React.Suspense fallback={<div></div>}>
        <BodyContainer>
          <Outlet />
        </BodyContainer>
        <Footer className={displayBottomPopup ? 'bottom-pop-active' : ''} />
      </React.Suspense>
      {displayBottomPopup && (
        <React.Suspense fallback={null}>
          <BottomPopup />
        </React.Suspense>
      )}
      {popupEnabled('center') && (
        <React.Suspense fallback={null}>
          <CenterPopup />
        </React.Suspense>
      )}
    </>
  );
};

export const WithHeaderWithoutFooterLayout = () => {
  const { popupEnabled } = useDisplayPopupItem();
  const { displayBottomPopup } = useDisplayBottomPopup();

  return (
    <>
      <Header />
      <HeaderPopup />
      <React.Suspense fallback={<div></div>}>
        <BodyContainer>
          <Outlet />
        </BodyContainer>
        <Footer hidden={true} />
      </React.Suspense>
      {displayBottomPopup && (
        <React.Suspense fallback={null}>
          <BottomPopup />
        </React.Suspense>
      )}
      {popupEnabled('center') && (
        <React.Suspense fallback={null}>
          <CenterPopup />
        </React.Suspense>
      )}
    </>
  );
};

export const MainPageLayout = () => {
  const { popupEnabled } = useDisplayPopupItem();
  const { displayBottomPopup } = useDisplayBottomPopup();
  const { showMobileMenu } = useZstdInfo();
  const { isMobile } = useWinResize();
  const isOver = useScrollOver(300);

  return (
    <MainWrap>
      <MainHeader />
      {/* 메뉴 열림에 의한 Header 노출은 실제 모바일(≤764px)에서만. 태블릿/데스크톱은 스크롤(isOver)만 */}
      <Header absolute hideHeader={!(isOver || (showMobileMenu && isMobile))} />
      <HeaderPopup />
      <React.Suspense fallback={<div></div>}>
        <BodyContainer>
          <Outlet />
        </BodyContainer>
        <Footer className={displayBottomPopup ? 'bottom-pop-active' : ''} />
      </React.Suspense>
      {displayBottomPopup && (
        <React.Suspense fallback={null}>
          <BottomPopup />
        </React.Suspense>
      )}
      {popupEnabled('center') && (
        <React.Suspense fallback={null}>
          <CenterPopup />
        </React.Suspense>
      )}
    </MainWrap>
  );
};

export const WithoutHeaderLayout = () => {
  return (
    <>
      <Header hideHeader={true} />
      <HeaderPopup />
      <React.Suspense fallback={<div></div>}>
        <BodyContainer>
          <Outlet />
        </BodyContainer>
      </React.Suspense>
    </>
  );
};

export const SuspenseLayout = () => {
  return (
    <React.Suspense fallback={<div></div>}>
      <Outlet />
    </React.Suspense>
  );
};

// tests/* 라우트는 svrConf.activeProfile에 따라 element 분기.
// (createBrowserRouter는 router 정의 단계에서 hook 호출 불가하므로 element 안에서 처리.)
const TestRouteElement = () => {
  const { svrConf } = useSvrInfo();
  return svrConf?.activeProfile !== 'prod' ? <TestLayout /> : <div />;
};

// React Router v7 createBrowserRouter + RootLayout.
// 함수 wrapper로 감싸 module init 시점에 layout 컴포넌트 const TDZ 충돌을 피한다.
// App 컴포넌트가 React.useMemo로 1회 호출하여 router instance를 안정화한다.
export const createAppRouter = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RootLayout />}>
        {/* Main Page Routes */}
        <Route element={<MainPageLayout />}>
          <Route index element={<MainPage />} />
          <Route path=':country/:lang/main' element={<MainPage />} />
          <Route path=':country/:lang/login' element={<MainPage />} />
        </Route>

        {/* WithHeader + WithoutFooter Routes */}
        <Route element={<WithHeaderWithoutFooterLayout />}>
          <Route path=':country/:lang/dlcenter' element={<DlCenterPage />} />
          <Route path=':country/:lang/community/partner-v2' element={<PtnrPageV2 />} />

          {/* 고정 높이 셸(본문 자체 스크롤 없음)을 쓰므로 Footer 를 노출하지 않는다 */}
          <Route path=':country/:lang/productselector-v2' element={<ProductSelectorV2 />} />
          <Route path='productselector-v2' element={<ProductSelectorV2 />} />
        </Route>

        {/* WithoutHeader Routes */}
        <Route element={<WithoutHeaderLayout />}>
          <Route path=':country/:lang/product/manual/:categoryId' element={<ManualPage />} />
          <Route path='product/manual/:categoryId' element={<ManualPage />} />
          <Route path=':country/:lang/manual/document/:documentId' element={<ManualPage />} />
          <Route path='manual/document/:documentId' element={<ManualPage />} />
        </Route>

        {/* Suspense Only Routes */}
        <Route element={<SuspenseLayout />}>
          <Route path=':country/:lang/rd/:urlGroup/:urlId' element={<UrlLinkPage />} />
          <Route path='disp/dispPreRegist' element={<DispPreRegist />} />
          <Route path=':country/:lang/disp/dispPreRegist' element={<DispPreRegist />} />
          <Route path='verifyEmail/:email' element={<VerifyEmailPage />} />
          <Route path='resetPassword/:email/:resetPassword' element={<ResetPasswordPage />} />
          <Route path='loginCallback' element={<LoginCallbackPage />} />
          <Route path='naverLoginCallback' element={<NaverLoginCallBackPage />} />
          <Route path='socialSignupPage' element={<SocialSignupPage />} />
          <Route path='unsupportedBrowserPage' element={<UnsupportedBrowserPage />} />
          <Route path='tests/*' element={<TestRouteElement />} />
        </Route>

        {/* Asset Routes - Redirect to Aseet insight(외부) */}
        <Route element={<SuspenseLayout />}>
          <Route path='asset/*' element={<AssetExternalRedirect />} />
          <Route path=':country/:lang/asset/*' element={<AssetExternalRedirect />} />
        </Route>

        {/* WithHeader Routes - Main section */}
        <Route element={<WithHeaderLayout />}>
          {/* Product routes */}
          <Route path=':country/:lang/product/category/:categoryId' element={<ProductPage />} />
          <Route path='product/category/:categoryId' element={<ProductPage />} />
          <Route path=':country/:lang/product/document/:documentId' element={<ProdDocDetailPage />} />
          <Route path='product/document/:documentId' element={<ProdDocDetailPage />} />

          {/* Solution routes */}
          <Route path=':country/:lang/solution/category/:categoryId' element={<SolutionPage />} />
          <Route path='solution/category/:categoryId' element={<SolutionPage />} />
          <Route path=':country/:lang/solution/document/:documentId' element={<SolutionPagePopup />} />
          <Route path='solution/document/:documentId' element={<SolutionPagePopup />} />

          {/* Selectors */}
          <Route path=':country/:lang/productselector' element={<ProductSelector />} />
          <Route path='productselector' element={<ProductSelector />} />
          <Route path=':country/:lang/drive-panel-selector' element={<DrivePanelSelector />} />
          <Route path='drive-panel-selector' element={<DrivePanelSelector />} />
          <Route path='productSelectorPubl' element={<ProductSelectorPubl />} />

          <Route path=':country/:lang/servo/purchase' element={<PurchasePage />} />

          {/* Service routes */}
          <Route path=':country/:lang/service/energysave' element={<EnergySave />} />
          <Route path='service/energysave' element={<EnergySave />} />
          <Route path=':country/:lang/service/assetinsight' element={<AssetInsight />} />
          <Route path='service/assetinsight' element={<AssetInsight />} />
          <Route path=':country/:lang/swservice' element={<SwService />} />
          <Route path='swservice' element={<SwService />} />
          <Route path=':country/:lang/service/sample/document/:documentId' element={<SamplePagePopup />} />
          <Route path='service/sample/document/:documentId' element={<SamplePagePopup />} />
          <Route path=':country/:lang/service/software/document/:documentId' element={<SwServicePagePopup />} />
          <Route path='service/software/document/:documentId' element={<SwServicePagePopup />} />
          <Route path=':country/:lang/service/:subMenu' element={<ServicePage />} />
          <Route path='service/:subMenu' element={<ServicePage />} />
          <Route path='us/en/privacypolicy' element={<PrivacyPolicyPage />} />
          <Route path='us/en/privacypolicy/:documentId' element={<PrivacyPolicyPage />} />
          <Route path='ww/en/compliance/ppwr' element={<PpwrPage />} />
          <Route path='ww/en/compliance/cra' element={<CraPage />} />
          <Route
            path=':country/:lang/privacypolicy/new/:documentId'
            element={
              <AdminWrap>
                <PrivacyPolicyWritePage />
              </AdminWrap>
            }
          />

          {/* Community routes */}
          <Route path=':country/:lang/community/partner/qna/document/:documentId' element={<PartnerQnaPagePopup />} />
          <Route path='community/partner/qna/document/:documentId' element={<PartnerQnaPagePopup />} />
          <Route path=':country/:lang/community/qna/document/:documentId' element={<QnaPagePopup />} />
          <Route path='community/qna/document/:documentId' element={<QnaPagePopup />} />
          <Route path=':country/:lang/community/rnd/qna/document/:documentId' element={<QnaPagePopup />} />
          <Route path='community/rnd/qna/document/:documentId' element={<QnaPagePopup />} />
          <Route path=':country/:lang/community/blog/document/:documentId' element={<BlogPagePopup />} />
          <Route path='community/blog/document/:documentId' element={<BlogPagePopup />} />
          <Route path=':country/:lang/community/rnd/notice/document/:documentId' element={<BlogPagePopup />} />
          <Route path='community/rnd/notice/document/:documentId' element={<BlogPagePopup />} />
          <Route path=':country/:lang/community/partner/notice/document/:documentId' element={<BlogPagePopup />} />
          <Route path='community/partner/notice/document/:documentId' element={<BlogPagePopup />} />
          <Route path=':country/:lang/community/board/document/:documentId' element={<BoardPagePopup />} />
          <Route path='community/board/document/:documentId' element={<BoardPagePopup />} />
          <Route path=':country/:lang/community/rnd/talk/document/:documentId' element={<BoardPagePopup />} />
          <Route path='community/rnd/talk/document/:documentId' element={<BoardPagePopup />} />
          <Route path=':country/:lang/community/:subMenu' element={<CommunityPage />} />
          <Route path='community/:subMenu' element={<CommunityPage />} />

          {/* Service Document Detail */}
          <Route path=':country/:lang/community/partner-v2/svc/document/:documentId' element={<SvcDocDetailPage />} />
          <Route
            path=':country/:lang/community/partner-v2/tech/document/:documentId'
            element={<TechSupportDetailPage />}
          />

          {/* Community Doc Write */}
          <Route
            path='boris/new/community/:orgDocId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path=':country/:lang/community/new/:orgDocId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path=':country/:lang/community/temp/:tempId/:docId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path=':country/:lang/community/approval/:docId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path='community/new/:orgDocId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path='community/temp/:tempId/:docId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path='community/approval/:docId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />
          <Route
            path='community/copy/:copyDocId'
            element={
              <AdminWrap className='Admin_wrap'>
                <CmmntyDocWritePage />
              </AdminWrap>
            }
          />

          {/* Search */}
          <Route path='newSearch' element={<AllSearchPage2 />} />
          <Route path=':country/:lang/newSearch' element={<AllSearchPage2 />} />

          <Route path='search3' element={<AllSearchPage3 />} />
          <Route path=':country/:lang/search3' element={<AllSearchPage3 />} />

          {/* MyPage */}
          <Route path=':country/:lang/myPage' element={<ProfilePage />} />
          <Route path='myPage' element={<ProfilePage />} />

          {/* Login require pages */}
          <Route path='requireMemberLogin' element={<RequireMemberLoginPage />} />
          <Route path=':country/:lang/requireMemberLogin' element={<RequireMemberLoginPage />} />
          <Route path='requireEmployeeLogin' element={<RequireEmployeeLoginPage />} />
          <Route path=':country/:lang/requireEmployeeLogin' element={<RequireEmployeeLoginPage />} />
          <Route path='requirePartnerLogin' element={<RequirePartnerLoginPage />} />
          <Route path=':country/:lang/requirePartnerLogin' element={<RequirePartnerLoginPage />} />

          {/* Boris/Admin routes */}
          <Route
            path='borisPage'
            element={
              <AdminWrap className='Admin_wrap'>
                <AdminPage2 />
              </AdminWrap>
            }
          />
          <Route
            path='boris/sw/new/:originalSwId'
            element={
              <AdminWrap className='Admin_wrap'>
                <AdminSwRegisterPage />
              </AdminWrap>
            }
          />
          <Route
            path='boris/sw/new'
            element={
              <AdminWrap className='Admin_wrap'>
                <AdminSwRegisterPage />
              </AdminWrap>
            }
          />
          <Route path='boris/ideaEventPage' element={<AdminEventDocumentListPage />} />
          <Route path='boris/ideaEventPage/RestrictedReviewerOnly' element={<AdminEventDocumentListReviewPage />} />
          <Route
            path='boris/event/new/document/:originalDocumentId'
            element={
              <AdminWrap className='Admin_wrap'>
                <AdminNewEventDocumentPage />
              </AdminWrap>
            }
          />
          <Route path='boris/event/document/:originalDocumentId' element={<EventReviewPopupPage />} />

          {/* 404 Not Found - must be last */}
          <Route path='*' element={<NotFoundPage />} />
        </Route>
      </Route>
    )
  );

const BodyContainer = styled.div`
  position: relative;

  button {
    ${fontFamilyRegular};
  }

  .batch-button {
    cursor: pointer;
    color: white;
    border-radius: 4px;
    width: 11.2rem;
    height: 4rem;
    line-height: 4rem;
    font-size: 1.3rem;
    background: #7783a1;
    border: solid 1px #7783a1;
    text-align: center;

    ${fontFamilyBold}
    + .batch-button {
      margin-left: 0.8rem;
    }

    &:hover {
      background: #6b758e;
    }

    &:active {
      background: #596278;
    }
  }

  .white-button {
    cursor: pointer;
    color: #2a2c2f;
    border-radius: 4px;
    width: 11.2rem;
    height: 4rem;
    line-height: 4rem;
    font-size: 1.3rem;
    background: white;
    border: solid 1px #c7d6ea;
    text-align: center;

    ${fontFamilyBold}
  }

  .post-button {
    cursor: pointer;
    color: white;
    width: 11.2rem;
    height: 4rem;
    line-height: 4rem;
    ${fontFamilyBold};
    border-radius: 0.4rem;
    font-size: 1.3rem;
    text-align: center;
    background: #407edb;
    border: solid 1px #407edb;

    &:hover {
      background: #3b71c4;
    }

    &:active {
      background: #3463ab;
    }
  }

  .delete-button {
    background: #fff !important;
    border: 1px solid #c7d6ea !important;
    color: #2a2c2f !important;
    ${fontFamilyMedium};

    &:hover {
      background: rgba(64, 125, 217, 0.1) !important;
    }

    &:active {
      background: rgba(64, 125, 217, 0.3) !important;
    }
  }
`;
const AdminWrap = styled.div`
  .approval_row {
    background: #fff;
    padding: 2.4rem 3.2rem;

    .approval_tit {
      font-size: 1.2rem;
      line-height: 3.2rem;
      width: 9rem;
      min-width: 8rem;
    }

    .commu_select_wrap {
      display: flex;
    }

    @media screen and (max-width: 413px) {
      .approval_tit {
        width: auto;
      }

      .commu_select_wrap {
        flex-wrap: wrap;

        > button {
          margin-left: 0;
          margin-top: 0.8rem;
        }
      }
    }

    .approval_text {
      color: #2a2c2f;
      font-size: 1.2rem;
      font-weight: 500;
      line-height: 3.2rem;
    }

    .approval_icon {
      display: flex;
      justify-content: start;
      align-items: center;
      line-height: 3.2rem;

      > span {
        height: 1.6rem;
        line-height: 1.6rem;
        font-size: 10px;
        font-weight: 500;
        display: flex;
        justify-content: start;
        align-items: center;
        margin-right: 0.4rem;
      }

      > span:before {
        content: '';
        background-size: 100% auto;
        display: inline-block;
        height: 1.6rem;
        line-height: 1.6rem;
        width: 1.6rem;
        margin-right: 0.4rem;
      }

      > span:first-child {
        height: 2.4rem;
        line-height: 2.4rem;
        font-size: 10px;
        font-weight: 500;
        display: flex;
        justify-content: start;
        align-items: center;
        margin-right: 0.4rem;
      }

      > span:first-child:before {
        content: '';
        background-size: 100% auto;
        display: inline-block;
        height: 2.4rem;
        line-height: 2.4rem;
        width: 2.4rem;
        margin-right: 0.4rem;
      }

      .commufeed_icon {
        margin-right: 0.8rem;
      }

      .commufeed_icon:before {
        background: url(${SsqImages.boris.allMappedFeedbacks()});
      }

      .commusmail_icon {
        color: #407dd8;
      }

      .commusmail_icon:before {
        background: url(${SsqImages.community.icon.communitySmile16()});
      }

      .commuangry_icon {
        color: #ff215d;
      }

      .commuangry_icon:before {
        background: url(${SsqImages.community.icon.communityAngry16()});
      }

      .commualarm_icon {
        color: #ff215d;
      }

      .commualarm_icon:before {
        background: url(${SsqImages.community.icon.communityAlarm()});
      }
    }

    select {
      background-position: center right 0.8rem;
      width: 16rem;
      padding: 0 1.2rem;
      color: #2a2c2f;
    }

    select:disabled {
      background-color: rgba(199, 214, 234, 0.5);
      color: #2a2c2f;
    }

    select:disabled:active {
      background: rgba(199, 214, 234, 0.5) url(${SsqImages.common.icon.select()}) no-repeat center right 0.8rem;
    }
  }

  .radio_wrap input[type='radio'] {
    display: none;

    + label {
      position: relative;
      display: inline-block;
      width: 7rem;
      height: 3.2rem;
      line-height: 3.2rem;
      background: #fff;
      border: 1px solid #c7d6ea;
      text-align: center;
      margin-left: -1px;
      font-size: 1.2rem;
      color: #2a2c2f;
      cursor: pointer;
    }

    &:checked + label {
      background: #407dd9 !important;
      color: #fff !important;
      border: solid 1px #407dd9 !important;
      z-index: 1;
      opacity: 1;
    }
  }

  .error-message {
    color: #e6003e;
    position: absolute;
    left: 4px;
    bottom: 0;
    font-size: 10px;
  }

  .input-error {
    border: 1px solid #e6003e !important;
    position: relative;
  }

  .radio_wrap span:first-of-type label {
    border-radius: 4px 0 0 4px;
  }

  .radio_wrap span:last-child input + label {
    border-radius: 0 4px 4px 0;
  }

  .flex_row {
    clear: both;
    height: 8rem;

    > div {
      float: left;
      display: flex;
      width: 30%;
    }

    > div:first-child:not(.approval_tit) {
      width: 40%;
    }

    + .flex_row {
      margin-top: 1px;
    }
  }

  .approval_state {
    line-height: 3.2rem;
    font-size: 1.2rem;

    img {
      margin-right: 0.4rem;
      vertical-align: sub;
    }

    .name,
    .time {
      color: #7783a1;
      font-size: 1rem;
    }

    .name {
      margin: 0.8rem;
    }
  }

  .editor_wrap {
    margin: 1.6rem 0;
    width: 84rem;

    .editor_tit {
      background: #fff;
      width: 100%;
      height: 6.4rem;
      padding: 0 3.2rem;

      input {
        ${fontFamilyMedium};
        width: 100%;
        height: 3.2rem;
        margin: 1.6rem 0px;
        font-size: 1.8rem;

        ::placeholder {
          ${fontFamilyRegular};
          font-size: 1.3rem;
          color: #7783a1;
        }
      }
    }

    .editor_txt {
      max-width: 100%;

      .cke_chrome {
        border: none;
        max-width: 100%;
      }
    }

    .cke_top {
      background: #f5f8fd;
      border-bottom: none;
      padding: 6px 6px 2px;

      .cke_button {
        padding: 5px;

        &:hover,
        &:active {
          border: none !important;
          background: rgba(64, 126, 219, 0.1) !important;
        }
      }

      .cke_button_on {
        border: none !important;
        background: rgba(64, 126, 219, 0.3) !important;
      }

      .cke_combo_button {
        &:hover,
        &:active {
          border: none !important;
          background: rgba(64, 126, 219, 0.1) !important;
        }
      }
    }

    .input-error + .input-error {
      border-top: none !important;
    }
  }

  .add_btn {
    color: #407edb;
    ${fontFamilyBold};
    font-size: 1.2rem;
    line-height: 16px;
    margin-top: 1rem;

    img {
      margin-right: 0.8rem;
      vertical-align: middle;
    }
  }

  .category_row {
    padding: 2.4rem 3.2rem;

    .approval_tit {
      margin-bottom: 1.6rem;
      line-height: 2.4rem;
      width: auto;

      > span {
        font-size: 1rem;
        color: #7783a1;
        margin-left: 3rem;
        ${fontFamilyRegular};
      }
    }

    .category_select_level_wrap {
      + div {
        margin-top: 1.2rem;
      }

      select + select {
        margin-left: 0.8rem;
      }
    }

    .category_item {
      display: flex;

      + .category_item {
        margin-top: 1.2rem;
      }
    }

    .add_btn img {
      vertical-align: bottom;
    }
  }

  .category_chk_row {
    display: flex;
    justify-content: space-between;
    border-bottom: solid 1px #eef1f6;
    padding-bottom: 2.4rem;

    + .category_chk_row {
      padding-top: 2.4rem;
    }

    .category_chk {
      width: calc(100% - 11rem);
      display: flex;
      flex-wrap: wrap;
      margin-bottom: -1.2rem;
    }

    input[type='checkbox'] {
      display: none;

      + label {
        cursor: pointer;
        position: relative;
        display: inline-block;
        width: 12rem;
        height: 3.2rem;
        font-size: 1.2rem;
        line-height: 3.2rem;
        background: #ffffff;
        border: 1px solid #c7d6ea;
        text-align: center;
        border-radius: 100px;
        margin-right: 0.8rem;
        margin-bottom: 1.2rem;
      }

      &:checked + label {
        background: #407dd9;
        color: #fff;
        border: solid 1px #407dd9;
        z-index: 1;
        ${fontFamilyBold}
      }
    }
  }

  .Version_wrap {
    border: none;

    > div {
      width: 84rem;
    }

    .Version_item {
      .Version_tit {
        font-size: 1.6rem;
        ${fontFamilyBold};
        margin-bottom: 2.4rem;
      }

      .Version_desc {
        font-size: 1.2rem;
        ${fontFamilyRegular};
        line-height: 1.5;

        a:link,
        a:visited {
          font-size: 1.5rem;
          color: #0782c1;
        }
      }

      .Version_desc p {
        font-size: 1.5rem;
      }

      .Version_desc h1 {
        font-size: 18px;
        color: #407edb;
        margin-bottom: 4px;

        img {
          width: 100%;
          height: auto;
        }
      }

      .Version_desc h2 {
        font-size: 16px;
        color: #2a2c2f;
        padding: 0;
        margin-bottom: 4px;

        img {
          width: 100%;
          height: auto;
        }
      }

      .Version_desc h3 {
        font-size: 15px;
        color: #2a2c2f;
        margin-bottom: 4px;

        img {
          width: 100%;
          height: auto;
        }
      }

      .Version_desc hr {
        border: 0px;
        border-top: 1px solid #c7d6ea;
        margin: 10px 0;
        height: 0px;
      }

      .Version_desc .table table {
        border-collapse: collapse;
        border-color: #c7d6ea;
        border-top: solid 2px #407dd9;
        border-bottom: solid 1px #90a0b6;
        border-left: none;
        border-right: none;
        max-width: 100%;
      }

      .Version_desc .table table th,
      .Version_desc .table table td {
        border: solid 1px #c7d6ea;
        background-color: transparent;
        padding: 7px 8px;
        text-align: left;
      }

      .Version_desc .table .ck-table-resized {
        table-layout: fixed;
        width: 100%;
      }

      .Version_desc .subtitle {
        color: #7783a1;

        img {
          width: 100%;
          height: auto;
        }
      }

      .Version_desc mark.marker-yellow {
        background: linear-gradient(to top, #fffaa0 50%, transparent 50%);
      }

      .Version_desc .table tr:last-child th,
      .Version_desc .table tr:last-child td {
        border-bottom: none;
      }

      .Version_desc .table table p {
        padding: 0px;
        margin: 0px;
      }

      .Version_desc span > img {
        margin: auto;
      }

      .cke_widget_embed img {
        margin: 0;
      }

      @media screen and (max-width: 764px) {
        .Version_desc .table table {
          width: 100% !important;
          max-width: 100%;
          table-layout: fixed;
          word-break: break-word;
        }
      }

      .Version_desc u {
        text-decoration: underline;
      }

      .Version_desc .table > figcaption {
        caption-side: top;
        word-break: break-word;
        text-align: center;
        padding: 0.6em;
      }

      .Version_desc .media {
        clear: both;
        margin: 0.9em 0;
        display: block;
        min-width: 15em;
      }

      .Version_desc .image {
        display: table;
        clear: both;
        text-align: center;
        margin: 0.9em auto;
        min-width: 50px;
      }

      .Version_desc .image img {
        display: block;
        margin: 0 auto;
        max-width: 100%;
        min-width: 100%;
      }

      .Version_desc .image-inline {
        display: inline-flex;
        max-width: 100%;
        align-items: flex-start;
      }

      .Version_desc .image-inline picture {
        display: flex;
      }

      .Version_desc .image-inline picture,
      .Version_desc .image-inline img {
        flex-grow: 1;
        flex-shrink: 1;
        max-width: 100%;
      }

      .Version_desc .image.image_resized {
        max-width: 100%;
        display: block;
        box-sizing: border-box;
      }

      .Version_desc .image.image_resized img {
        width: 100%;
      }

      .Version_desc .image.image_resized > figcaption {
        display: block;
      }

      .Version_desc .marker-yellow {
        background-color: var(--ck-highlight-marker-yellow);
      }

      .Version_desc .marker-green {
        background-color: var(--ck-highlight-marker-green);
      }

      .Version_desc .marker-pink {
        background-color: var(--ck-highlight-marker-pink);
      }

      .Version_desc .marker-blue {
        background-color: var(--ck-highlight-marker-blue);
      }

      .Version_desc .pen-red {
        color: var(--ck-highlight-pen-red);
        background-color: transparent;
      }

      .Version_desc .pen-green {
        color: var(--ck-highlight-pen-green);
        background-color: transparent;
      }

      .Version_desc ol {
        list-style-type: decimal;
      }

      .Version_desc ol ol {
        list-style-type: lower-latin;
      }

      .Version_desc ol ol ol {
        list-style-type: lower-roman;
      }

      .Version_desc ol ol ol ol {
        list-style-type: upper-latin;
      }

      .Version_desc ol ol ol ol ol {
        list-style-type: upper-roman;
      }

      .Version_desc ul {
        list-style-type: disc;
      }

      /* @ckeditor/ckeditor5-list/theme/list.css */

      .Version_desc ul ul {
        list-style-type: circle;
      }

      .Version_desc ul ul ul {
        list-style-type: square;
      }

      .Version_desc ul ul ul ul {
        list-style-type: square;
      }

      .Version_desc .image-style-block-align-left,
      .Version_desc .image-style-block-align-right {
        max-width: calc(100% - var(--ck-image-style-spacing));
      }

      .Version_desc .image-style-align-left,
      .Version_desc .image-style-align-right {
        clear: none;
      }

      .Version_desc .image-style-side {
        float: right;
        margin-left: var(--ck-image-style-spacing);
        max-width: 50%;
      }

      .Version_desc .image-style-align-left {
        float: left;
        margin-right: var(--ck-image-style-spacing);
      }

      .Version_desc .image-style-align-center {
        margin-left: auto;
        margin-right: auto;
      }

      .Version_desc .image-style-align-right {
        float: right;
        margin-left: var(--ck-image-style-spacing);
      }

      .Version_desc .image-style-block-align-right {
        margin-right: 0;
        margin-left: auto;
      }

      .Version_desc .image-style-block-align-left {
        margin-left: 0;
        margin-right: auto;
      }

      .Version_desc p + .image-style-align-left,
      .Version_desc p + .image-style-align-right,
      .Version_desc p + .image-style-side {
        margin-top: 0;
      }

      .Version_desc .image-inline.image-style-align-left,
      .Version_desc .image-inline.image-style-align-right {
        margin-top: var(--ck-inline-image-style-spacing);
        margin-bottom: var(--ck-inline-image-style-spacing);
      }

      .Version_desc .image-inline.image-style-align-left {
        margin-right: var(--ck-inline-image-style-spacing);
      }

      .Version_desc .image-inline.image-style-align-right {
        margin-left: var(--ck-inline-image-style-spacing);
      }

      .Version_desc code {
        background-color: hsla(0, 0%, 78%, 0.3);
        padding: 0.15em;
        border-radius: 2px;
      }

      .Version_desc blockquote {
        overflow: hidden;
        padding: 8px 16px;
        margin-left: 0;
        margin-right: 0;
        font-style: normal;
        font-size: 15px;
        border-left: solid 2px #407dd9;
        background-color: #f7f8fb;
      }

      .Version_desc .text-tiny {
        font-size: 0.7em;
      }

      .Version_desc .text-small {
        font-size: 0.85em;
      }

      .Version_desc .text-big {
        font-size: 1.4em;
      }

      .Version_desc .text-huge {
        font-size: 1.8em;
      }

      .Version_desc pre {
        padding: 1em;
        color: hsl(0, 0%, 20.8%);
        background: hsla(0, 0%, 78%, 0.3);
        border: 1px solid hsl(0, 0%, 77%);
        border-radius: 2px;
        text-align: left;
        direction: ltr;
        tab-size: 4;
        white-space: pre-wrap;
        font-style: normal;
        min-width: 200px;
      }

      .Version_desc pre code {
        background: unset;
        padding: 0;
        border-radius: 0;
      }

      + .file_info {
        border-top: 1px dashed #90a0b6;
        margin-top: 2.4rem;
        padding-top: 2.4rem;
      }
    }

    .file_info {
      .file_name {
        color: #407edb;
        font-size: 1.3rem;
        display: inline-block;
        cursor: pointer;
      }

      button {
        vertical-align: middle;
        margin: 0px 0.7rem;
        cursor: pointer !important;
      }

      > div {
        display: inline-block;
      }

      span {
        font-size: 1.2rem;
        color: #7783a1;
      }

      span:after {
        content: '';
        width: 1px;
        height: 1.5rem;
        background: #c7d6ea;
        margin: 0 0.5rem;
        display: inline-block;
        vertical-align: middle;
      }

      span:last-child:after {
        display: none;
      }

      span b {
        color: #407edb;
        font-weight: normal;
        margin-left: 0.5rem;
      }

      + .file_info {
        margin-top: 0.4rem;
      }
    }

    + .Version_wrap {
      border-top: solid 1px #eef1f6;
    }
  }

  .file_select {
    display: inline-block;
    margin-left: 2.7rem;

    select {
      background-position: center right 0.8rem;
      width: 16rem;
      padding: 0 1.2rem;
      color: #2a2c2f;

      + select {
        margin-left: 0.8rem;
      }
    }

    select:disabled {
      background-color: rgba(199, 214, 234, 0.5);
      color: #2a2c2f;
    }

    select:disabled:active {
      background: rgba(199, 214, 234, 0.5) url(${SsqImages.common.icon.select()}) no-repeat center right 0.8rem;
    }
  }

  .approval_wrap .category_row {
    padding: 2.4rem 0 0;
  }

  .reject_popup {
    .popup-content {
      font-size: 1.2rem;

      input {
        border: 1px solid #c7d6ea;
        border-radius: 4px;
        width: 100%;
        height: 20rem;
        padding: 0 0.8rem;
        margin-top: 0.4rem;

        ::placeholder {
          color: #7783a1;
          font-size: 1rem;
        }
      }
    }
  }

  @media screen and (max-width: 1199px) and (min-width: 765px) {
    overflow-x: auto;
    > div:first-of-type {
      width: 120rem;
    }

    .Admin_newdocument_wrap,
    .Admin_approval_wrap {
      padding: 1.6rem 3rem;
    }

    .editor_wrap,
    .editor_txt .cke_chrome {
      width: 100% !important;
    }

    .Version_edit_wrap .editor_wrap {
      width: 84rem !important;
    }

    .cke_bottom {
      display: none !important;
    }

    .file_select {
      margin-left: 2.4rem;
      margin-top: 0.6rem;
    }

    .yes-no-btn {
      display: flex;
      flex-wrap: nowrap;
    }

    .category_select_level {
      //display: flex;
      //flex-wrap: nowrap;
    }
  }

  @media screen and (max-width: 920px) and (min-width: 765px) {
    .template-wrap {
      flex-wrap: wrap;
    }

    .product-list select + select {
      margin-top: 0.8rem !important;
    }
  }
  @media screen and (max-width: 764px) {
    overflow-x: auto;
    > div:first-of-type {
      width: 120rem;
    }

    .Admin_newdocument_wrap,
    .Admin_approval_wrap {
      width: auto !important;
      padding: 2rem 0;

      .approval_row {
        padding: 1.6rem !important;

        .approval_tit {
          width: 8.5rem;
        }

        .file_info div {
          display: block;
        }
      }

      .flex_row {
        height: auto;

        > div {
          float: none;
          width: 100% !important;

          + div {
            margin-top: 1.6rem;
          }
        }
      }

      .category_chk_row input[type='checkbox'] + label {
        width: 11.3rem;
      }

      .editor_wrap .editor_tit {
        padding: 0 1.6rem;
      }

      .editor_wrap .cke_toolbox {
        display: flex;
        flex-wrap: wrap;
      }

      .approval_wrap {
        padding: 0 1.6rem !important;

        > div {
          padding: 1.6rem 0px !important;
        }

        .approval_tit {
          width: auto;
          margin-bottom: 0.8rem;

          > span {
            display: block;
            margin-left: 0;
            line-height: 1.2;
          }
        }

        .category_chk_row {
          flex-direction: column;

          .category_chk {
            width: 100%;
            margin-top: 0.8rem;
          }
        }
      }

      .approval_wrap.app-new-area {
        padding: 0 !important;
      }

      .approval_wrap.app-new-area > div {
        padding: 0 !important;
      }

      .approval_wrap.category_row {
        padding: 1.6rem !important;

        > div {
          padding: 0px !important;
        }
      }

      .category_row {
        > div {
          padding: 0;
        }

        .category_item {
          align-items: baseline;
        }

        select {
          width: 20rem;
          display: block;

          + select {
            margin: 0.8rem 0 0 0;
          }
        }
      }

      .Version_wrap {
        > div {
          width: 100%;
        }
      }

      .version_row {
        .Version_history_wrap > div {
          padding: 1.6rem;
        }

        .file-item .file-name {
          width: 70%;
        }

        .file-radio {
          margin: 0.8rem 2.7rem;
          display: block;
        }

        .not-use-version {
          padding: 1.6rem;
        }
      }

      .Version_edit_wrap {
        padding: 1.6rem;

        .editor_wrap {
          width: auto !important;
        }
      }

      .Version_history_wrap {
        padding: 0;
      }
    }
  }
`;
